import { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useVendorStore } from '../stores/vendorStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, RefreshCw, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { downloadUploadTemplate, uploadBanksExcel, customImportExcel } from '../services/api';

const ResultsPanel = ({ results, isCustom }) => {
  if (!results) return null;
  return (
    <div className="space-y-3 border-t pt-4">
      <h4 className="text-sm font-semibold">Import Results</h4>
      <div className={`grid ${isCustom ? 'grid-cols-3' : 'grid-cols-4'} gap-3`}>
        <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
          <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
          <div className="text-xl font-bold text-green-700 mt-1" data-testid="upload-created-count">{results.created}</div>
          <div className="text-xs text-green-600">Created</div>
        </div>
        {!isCustom && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
            <RefreshCw className="w-5 h-5 text-blue-600 mx-auto" />
            <div className="text-xl font-bold text-blue-700 mt-1" data-testid="upload-updated-count">{results.updated || 0}</div>
            <div className="text-xs text-blue-600">Updated</div>
          </div>
        )}
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-center">
          <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto" />
          <div className="text-xl font-bold text-amber-700 mt-1" data-testid="upload-skipped-count">{results.skipped}</div>
          <div className="text-xs text-amber-600">Skipped</div>
        </div>
        <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
          <XCircle className="w-5 h-5 text-red-600 mx-auto" />
          <div className="text-xl font-bold text-red-700 mt-1" data-testid="upload-error-count">{results.errors?.length || 0}</div>
          <div className="text-xs text-red-600">Errors</div>
        </div>
      </div>

      {isCustom && (results.vendors_created?.length > 0 || results.branches_created?.length > 0) && (
        <div className="space-y-2">
          {results.vendors_created?.length > 0 && (
            <div className="text-xs"><span className="font-semibold text-blue-700">Vendors created:</span> {results.vendors_created.join(', ')}</div>
          )}
          {results.branches_created?.length > 0 && (
            <div className="text-xs"><span className="font-semibold text-indigo-700">Branches created:</span> {results.branches_created.slice(0, 15).join(', ')}{results.branches_created.length > 15 ? ` +${results.branches_created.length - 15} more` : ''}</div>
          )}
          {results.duplicates?.length > 0 && (
            <div className="text-xs"><span className="font-semibold text-amber-700">Duplicate accounts skipped:</span> {results.duplicates.length}</div>
          )}
        </div>
      )}

      {!isCustom && (results.details?.length > 0 || results.errors?.length > 0) && (
        <div className="max-h-48 overflow-y-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr><th className="text-left p-2 font-semibold">Row</th><th className="text-left p-2 font-semibold">Action</th><th className="text-left p-2 font-semibold">Details</th></tr>
            </thead>
            <tbody>
              {results.details?.map((d) => (
                <tr key={`detail-${d.row}-${d.action}`} className="border-t">
                  <td className="p-2">{d.row}</td>
                  <td className="p-2"><Badge className={d.action === 'created' ? 'bg-green-100 text-green-800' : d.action === 'updated' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>{d.action}</Badge></td>
                  <td className="p-2 text-slate-600">{d.bank && <span>{d.bank}</span>}{d.vendor && <span className="text-slate-400"> ({d.vendor})</span>}{d.reason && <span>{d.reason}</span>}{d.fields && <span>Updated: {d.fields.join(', ')}</span>}</td>
                </tr>
              ))}
              {results.errors?.map((e, i) => (
                <tr key={`err-${i}`} className="border-t bg-red-50"><td className="p-2">{e.row || i}</td><td className="p-2"><Badge className="bg-red-100 text-red-800">error</Badge></td><td className="p-2 text-red-700">{e.error || e}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCustom && results.errors?.length > 0 && (
        <div className="max-h-32 overflow-y-auto border border-red-200 rounded-lg p-2 bg-red-50">
          {results.errors.map((e, i) => <div key={i} className="text-xs text-red-700">{e}</div>)}
        </div>
      )}
    </div>
  );
};

const UploadBankDialog = ({ open, onOpenChange }) => {
  const user = useAuthStore((s) => s.user);
  const { vendors, fetchVendors } = useVendorStore();
  const [tab, setTab] = useState('custom');
  const [file, setFile] = useState(null);
  const [vendorId, setVendorId] = useState('');
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadUploadTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'Bank_Upload_Template.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) { console.error('Template download failed:', err.message); toast.error('Failed to download template'); }
  };

  const handleStandardUpload = async () => {
    if (!file) { toast.error('Please select an Excel file'); return; }
    setUploading(true); setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('duplicate_action', duplicateAction);
      if (vendorId) formData.append('vendor_id', vendorId);
      const res = await uploadBanksExcel(formData);
      setResults(res.data);
      fetchVendors();
      if (res.data.created > 0) toast.success(`${res.data.created} banks created`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleCustomImport = async () => {
    if (!file) { toast.error('Please select an Excel file'); return; }
    setUploading(true); setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await customImportExcel(formData);
      setResults(res.data);
      fetchVendors();
      toast.success(`Imported ${res.data.created} banks (${res.data.skipped} duplicates skipped)`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setUploading(false); }
  };

  const handleClose = () => { setFile(null); setVendorId(''); setDuplicateAction('skip'); setResults(null); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import Banks from Excel</DialogTitle>
          <DialogDescription>Upload an Excel file to bulk-import bank details into your account.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setFile(null); setResults(null); }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="custom" className="gap-1.5"><FileUp className="w-3.5 h-3.5" />Custom Import</TabsTrigger>
            <TabsTrigger value="standard" className="gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5" />Template Upload</TabsTrigger>
          </TabsList>

          <TabsContent value="custom" className="space-y-4 mt-4">
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm font-medium text-indigo-800">Smart Import</p>
              <p className="text-xs text-indigo-600 mt-0.5">Upload any Excel with bank data. The system auto-detects columns like <code className="bg-indigo-100 px-1 rounded">acc_num</code>, <code className="bg-indigo-100 px-1 rounded">ifsc_swift</code>, <code className="bg-indigo-100 px-1 rounded">company_name</code>, <code className="bg-indigo-100 px-1 rounded">status</code>, <code className="bg-indigo-100 px-1 rounded">assigned_branch</code>, etc. Vendors and branches are auto-created. Duplicates are skipped.</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Excel File</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setResults(null); }} className="mt-1" data-testid="custom-import-file-input" />
              {file && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                  <FileSpreadsheet className="w-4 h-4 text-green-600" /><span>{file.name}</span>
                  <Badge variant="outline" className="text-xs">{(file.size / 1024).toFixed(1)} KB</Badge>
                </div>
              )}
            </div>
            <Button onClick={handleCustomImport} disabled={!file || uploading} className="w-full gap-2" data-testid="custom-import-btn">
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Importing...' : 'Import Data'}
            </Button>
            <ResultsPanel results={results} isCustom={true} />
          </TabsContent>

          <TabsContent value="standard" className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Download Template</p>
                <p className="text-xs text-blue-600 mt-0.5">Get the Excel template with all supported columns</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="download-template-btn">
                <Download className="w-4 h-4" /> Template
              </Button>
            </div>
            <div>
              <Label className="text-sm font-medium">Excel File</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setResults(null); }} className="mt-1" data-testid="upload-file-input" />
              {file && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                  <FileSpreadsheet className="w-4 h-4 text-green-600" /><span>{file.name}</span>
                  <Badge variant="outline" className="text-xs">{(file.size / 1024).toFixed(1)} KB</Badge>
                </div>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium">Default Vendor (optional)</Label>
              <Select value={vendorId || 'none'} onValueChange={(v) => setVendorId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="upload-vendor-select"><SelectValue placeholder="Select vendor..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No default (use Excel column) --</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Duplicate Handling</Label>
              <Select value={duplicateAction} onValueChange={setDuplicateAction}>
                <SelectTrigger data-testid="duplicate-action-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="skip">Skip duplicates</SelectItem>
                  <SelectItem value="update">Update existing records</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleStandardUpload} disabled={!file || uploading} className="w-full gap-2" data-testid="upload-submit-btn">
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload & Process'}
            </Button>
            <ResultsPanel results={results} isCustom={false} />
          </TabsContent>
        </Tabs>

        <DialogFooter><Button variant="outline" onClick={handleClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadBankDialog;
